import { LoginRequiredError, InteractionRequiredError, HttpRequestFailedError, UnhandledError, type AuthErrorOptions } from '@bingads-webui-clientcenter/auth-errors';
import { get as cookieGet, set as cookieSet } from '@bingads-webui/cookie';
import IWindow, { type WindowInterface } from '@bingads-webui-clientcenter/window-interface';
import Promise from 'bluebird';
import _ from 'underscore';
import URI from 'urijs';

const cookieName = 'msaDelegate';
const valueParser = /(#|&)(access_token=([^&]*)|error=login_required|error=interaction_required)(&|$)/i;
const authType = 'msa';
const getUriQuery = (uri: { query(value: true): object }): Record<string, string> => uri.query(true) as Record<string, string>;

export interface MsaAuthSmartOptions {
  cookieDomain: string;
  cookiePath: string;
  delegateAuthUrl: string;
  customLogoutUrl?: string;
  getCurrentUrl?: () => string;
  setDomainEnabled?: boolean;
  shouldAuthenticateWithPopup?: boolean;
  shouldRetryWithPopup?: boolean;
}

export interface AuthenticateOptions {
  numTriesAllowed?: number;
}

export interface AuthenticateResolveObject {
  token: string;
  attempts: number;
}

export interface AuthRejectContext extends Pick<AuthErrorOptions, 'attempts' | 'debugInfo'> {
  debugMessage?: string;
}

export type HashHandler = (hashValue?: string, debugInfo?: string) => void;
export type PopupSuccessHandler = (hashValue?: string) => void;
export type PopupErrorHandler = () => void;

export type PopupWindow = WindowInterface & {
  left?: number;
  width?: number;
  height?: number;
  open(url?: string, target?: string, features?: string): Window;
};

type MsaAuthError = LoginRequiredError | InteractionRequiredError | HttpRequestFailedError | UnhandledError | string;
type TokenResolve = (token: string) => void;
type AuthReject = (error: MsaAuthError) => void;

function formatToken(token: string) {
  return `MSADELEGATE1.0=${token}`;
}

/**
 * @callback HashHandler
 * @param {string} hashCode - The hash value recieved from the msa delegate auth call.
 */

/**
 * Adds an iframe that passes the hash value to the given callback once it loads.
 * @param {string} delegateAuthUrl - The url of the iframe
 * @param {string} frameName - The name of the iframe.
 * @param {HashHandler} hashHandler - The handler for the hash value once the iframe loads.
 * @return {void}
 */
function addIframe(delegateAuthUrl: string, frameName: string, hashHandler: HashHandler) {
  if (IWindow.location.hostname.toLowerCase().indexOf('sdk') !== -1) {
    const onAuthCompleteHandler = (evt: MessageEvent) => {
      if (evt && evt.data && evt.data.type === 'MsaDelegateAuthForParentComplete') {
        const hashValue = evt.data.payload && evt.data.payload.hashValue;
        IWindow.removeEventListener('message', onAuthCompleteHandler);
        hashHandler(hashValue, '');
      }
    };
    IWindow.addEventListener('message', onAuthCompleteHandler);
  }

  const iframe = IWindow.document.createElement('iframe');
  iframe.name = frameName;
  iframe.style.display = 'none';
  iframe.onload = () => {
    if (IWindow.location.hostname.toLowerCase().indexOf('sdk') !== -1) {
      IWindow.document.body.removeChild(iframe);
    } else {
      let hashValue: string | undefined;
      let debugInfo: string | undefined;
      try {
        const frames = IWindow.frames as unknown as Record<string, Window>;
        debugInfo = `Window: ${IWindow}, Frame name: ${frameName}, Frame: ${frames[frameName]}`;
        const frame = frames[frameName];
        if (frame) {
          debugInfo = `Frame location: ${frame.location}`;
          hashValue = frame.location.hash;
          if (_.isUndefined(hashValue)) {
            debugInfo = `Got Frame, no hash. location: ${frame.location.href}`;
          }
        } else {
          debugInfo = 'Could not find frame';
        }
      } catch (e) {
        // Ignore the error. If there is an error, its a cross-origin fail because both frames didn't
        // set WindowAbstraction.document.domain. Leave hashValue undefined, and we will throw a HttpRequestFailedError
        debugInfo = `${debugInfo} - Error: ${JSON.stringify(e)}`;
      }

      IWindow.document.body.removeChild(iframe);
      hashHandler(hashValue, debugInfo);
    }
  };
  iframe.src = delegateAuthUrl;
  IWindow.document.body.appendChild(iframe);
}


function setMsaDelegateCookie(this: MsaAuthSmart, cookieValue: string, expireDate?: Date) {
  let cookieExpirationDate = expireDate;
  if (_.isUndefined(cookieExpirationDate)) {
    cookieExpirationDate = new Date();
    cookieExpirationDate.setHours(cookieExpirationDate.getHours() + 4);
  }

  cookieSet(cookieName, cookieValue, cookieExpirationDate, this.cookiePath, this.cookieDomain);
}

let onPopupLoadedHandler: ((evt: MessageEvent) => void) | null = null;
const RETRY_TIMEOUT = 5000;

export function authenticateWithPopup(delegateAuthUrl: string, onSuccess: PopupSuccessHandler, onError: PopupErrorHandler, glb: PopupWindow = IWindow as unknown as PopupWindow) {
  if (onPopupLoadedHandler) {
    glb.removeEventListener('message', onPopupLoadedHandler);
  }
  const windowWidth = 480;
  const windowHeight = 450;
  const dualScreenLeft = (glb.screenLeft === undefined ? glb.left : glb.screenLeft) as number;
  const dualScreenTop = (glb.screenTop === undefined ? glb.top : glb.screenTop) as number;
  const { documentElement } = document;
  const width = (glb.innerWidth || documentElement.clientWidth || glb.width) as number;
  const height = (glb.innerHeight || documentElement.clientHeight || glb.height) as number;
  const left = ((width - windowWidth) / 2) + dualScreenLeft;
  const top = ((height - windowHeight) / 2) + dualScreenTop;

  const callingDomain = `${glb.location.protocol}//${glb.location.hostname}`;
  const delegateAuthUrlObj = URI(delegateAuthUrl);
  let redirectUrl = getUriQuery(delegateAuthUrlObj).redirect_uri;
  const redirectUrlObj = URI(decodeURIComponent(redirectUrl));
  const cdParam = getUriQuery(redirectUrlObj).cd;
  if (!cdParam) {
    redirectUrlObj.addQuery('cd', callingDomain);
    redirectUrl = redirectUrlObj.toString();
    delegateAuthUrlObj.removeQuery('redirect_uri').addQuery('redirect_uri', redirectUrl);
  }

  const popupUrl = delegateAuthUrlObj.toString();

  const delegateMsaSignInPopup = glb.open(popupUrl, 'loginWindow', `resizable=yes, height=${windowHeight},width=${windowWidth},left=${left},top=${top}`);

  const errorTimeout = setTimeout(() => {
    onError();
  }, RETRY_TIMEOUT);

  onPopupLoadedHandler = (evt: MessageEvent) => {
    if (evt && evt.data && evt.data.type === 'MsaDelegateAuthComplete') {
      const hashValue = evt.data.payload && evt.data.payload.hashValue;
      glb.removeEventListener('message', onPopupLoadedHandler);
      delegateMsaSignInPopup.close();
      clearTimeout(errorTimeout);
      onSuccess(hashValue);
    }
  };
  glb.addEventListener('message', onPopupLoadedHandler);
}

function processHash(this: MsaAuthSmart, hashValue: string, resolve: TokenResolve, reject: AuthReject, rejectObject: AuthRejectContext, delegateAuthUrl: string, shouldRetryWithPopup?: boolean) {
  let response: string | undefined;
  let token: string | undefined;
  const match = valueParser.exec(hashValue);
  if (match) {
    [, , response, token] = match;
  }
  if (token) {
    setMsaDelegateCookie.apply(this, [token]);
    resolve(formatToken(token));
  // Retry with popup when there is `isSDK=true` in URL and the current call is not from a retry
  } else if (shouldRetryWithPopup) {
    this.authenticateWithPopupInternal(delegateAuthUrl, (hashValueFromPopup?: string) => {
      // use hashValue got from popup
      processHash.apply(this, [
        hashValueFromPopup,
        resolve,
        reject,
        rejectObject,
        delegateAuthUrl,
      ]);
    }, () => {
      // proceed with original hash value if error happened (timeout exceeded)
      processHash.apply(this, [
        hashValue,
        resolve,
        reject,
        rejectObject,
        delegateAuthUrl,
      ]);
    });
  } else if (response === 'error=login_required') {
    reject(new LoginRequiredError(_.extend(
      {
        authType,
        debugInfo: {
          valueReceived: hashValue,
        },
      },
      rejectObject
    )));
  } else if (response === 'error=interaction_required') {
    reject(new InteractionRequiredError({
      authType,
      attempts: (rejectObject && rejectObject.attempts) || 0,
    }));
  } else {
    reject(new UnhandledError(_.extend(
      {
        authType,
        debugInfo: {
          valueReceived: hashValue,
        },
      },
      rejectObject
    )));
  }
}

export default class MsaAuthSmart {
  cookieDomain: string;
  cookiePath: string;
  customLogoutUrl?: string;
  delegateAuthUrl: string;
  getCurrentUrl: () => string;
  setDomainEnabled: boolean;
  shouldAuthenticateWithPopup?: boolean;
  shouldRetryWithPopup?: boolean;
  constructor({
    cookieDomain,
    cookiePath,
    getCurrentUrl = () => IWindow.location.href,
    customLogoutUrl,
    delegateAuthUrl,
    setDomainEnabled,

    // If shouldRetryWithPopup is true, when initial iframe authentication failed, will retry the process in a seperate popup
    shouldRetryWithPopup,

    // If shouldAuthenticateWithPopup is true, we will use popup to do msa delegate authentication
    shouldAuthenticateWithPopup,
  }: MsaAuthSmartOptions) {
    if (!delegateAuthUrl) {
      throw new TypeError('delegateAuthUrl not set');
    }
    // TODO: if these aren't set, don't fail, just don't use cookies
    if (!cookiePath) {
      throw new TypeError('cookiePath not set');
    }
    if (!cookieDomain) {
      throw new TypeError('cookieDomain not set');
    }

    this.getCurrentUrl = getCurrentUrl;
    this.cookieDomain = cookieDomain;
    this.cookiePath = cookiePath;
    this.customLogoutUrl = customLogoutUrl;
    this.delegateAuthUrl = delegateAuthUrl;
    this.setDomainEnabled = _.isBoolean(setDomainEnabled) ? setDomainEnabled : true;
    this.shouldRetryWithPopup = shouldRetryWithPopup;
    this.shouldAuthenticateWithPopup = shouldAuthenticateWithPopup;
  }

  /**
   * Clears the saved msa auth value from the cookie.
   * @return {void}
   */
  clearMsaDelegateCookie = () => {
    setMsaDelegateCookie.apply(this, ['', new Date(0)]);
  };

  /**
   * Gets the hash cookie and handles the value appropriately
   * @param {string} [hashCookieName] - The key to get the hash cookie.
   * @return {void}
   */
  handleLoginCookie = (hashCookieName: string) => {
    // if hashCookieName is not defined, we will hit an error
    if (!hashCookieName) {
      throw new Error('hashCookieName must be defined');
    }

    const hashVal = hashCookieName && cookieGet(hashCookieName);

    if (hashVal) {
      cookieSet(hashCookieName, '', new Date(0), this.cookiePath, this.cookieDomain);
      this.getAuthTokenFromHash(hashVal);
    }
  };

  /**
   * Gets the msa auth token from the hash and process it.
   * Saves the token in the cookie if valid, lgs out of bingads if logged out of MSA, does nothing in other situations.
   * @param {string} [hashValue = window.location.hash] - The hash value to parse.
   * @return {void}
   */
  getAuthTokenFromHash = (hashValue = IWindow.location.hash) => {
    if (hashValue) {
      processHash.apply(this, [hashValue, _.noop, (error: MsaAuthError) => {
        if (error instanceof LoginRequiredError) {
          const currentUri = new URI(this.getCurrentUrl());

          let logoutUri;
          if (this.customLogoutUrl) {
            logoutUri = new URI(this.customLogoutUrl);
          } else {
            logoutUri = new URI(currentUri.origin())
              .segment('logout');
          }
          logoutUri = logoutUri
            .addSearch({
              reason: 'msaAuth',
              rurl: currentUri.href(),
            });
          IWindow.location.href = logoutUri.href();
        }
      }, this.delegateAuthUrl]);
    }
  };

  /**
   * Attempts to load token in hash of current page
   * @return {void}
   */
  handleFullPageRedirect = () => {
    const delegateAuthUri = new URI(this.delegateAuthUrl);
    const redirectUri = getUriQuery(delegateAuthUri).redirect_uri;
    const currentUri = new URI(this.getCurrentUrl());
    const redirectUriWithRurl = (new URI(redirectUri)).setQuery('rurl', currentUri.href()).href();
    IWindow.location.href = delegateAuthUri.setQuery('redirect_uri', redirectUriWithRurl).href();
  };

  /**
   * @typedef AuthenticateResolveObject
   * @type {object}
   * @param {string} token - The formatted authenticationToken.
   * @param {int} attempts - The number of tries taken to get the token.
   */

  /**
   * authenticate and provide authentication token, or error.
   * @param {int} numTriesAllowed - The amount of retries if HttpError occurs.
   * @return {Promise<AuthenticateResolveObject, LoginRequiredError|HttpRequestFailedError|UnhandledError>} - The authentication result
   */
  authenticate = ({
    numTriesAllowed = 1,
  }: AuthenticateOptions = {}) => {
    if (this.shouldAuthenticateWithPopup) {
      return this.authenticateWithPopup();
    }

    // Makes the document belong to the generic "bingads.microsoft.com" - needed for communication with CTP frames.
    // Has to be set here and in MSADelegateAuth iframe so that after a CTP frame has loaded,
    // MSADelegateAuth iframe can still communicate with the parent window
    if (this.setDomainEnabled || IWindow.location.hostname.toLowerCase().split('.').indexOf('ui') !== -1) {
      IWindow.document.domain = IWindow.document.domain.split('.').slice(-3).join('.');
    }

    return new Promise<AuthenticateResolveObject>((resolve, reject) => {
      const msaDelegateToken = cookieGet(cookieName);
      if (msaDelegateToken) {
        resolve({
          token: formatToken(msaDelegateToken),
          attempts: 0,
        });
        return;
      }

      let attempts = 1;
      const frameName = _.uniqueId('delegateAuth-');

      const delegateAuthUri = new URI(this.delegateAuthUrl);
      // a temp workaround because can't register new domains
      // after Yu do a migration to a new auth method, this should be deprecated.
      let redirectUri = getUriQuery(delegateAuthUri).redirect_uri;
      if (redirectUri.includes('sdk')) {
        redirectUri = redirectUri.replace(/sdk[a-z]*\./, '');
      }
      const redirectUriWithCd =
        (new URI(redirectUri)).setQuery('cd', IWindow.location.hostname).href();
      const delegateAuthUrlWithCd =
        delegateAuthUri.setQuery('redirect_uri', redirectUriWithCd).href();

      const handler: HashHandler = (hashValue, debugInfo) => {
        const reAddIframe = () => {
          attempts += 1;
          addIframe(delegateAuthUrlWithCd, frameName, handler);
        };
        // if a value wasn't received (i.e. MSADelegateAuth did not load correctly)
        if (_.isUndefined(hashValue)) {
          if (attempts < numTriesAllowed) {
            reAddIframe();
          } else {
            reject(new HttpRequestFailedError({ authType, attempts, debugInfo }));
          }
          return;
        }

        const errorHandler = (error: MsaAuthError) => {
          if (attempts < numTriesAllowed && error instanceof InteractionRequiredError) {
            reAddIframe();
          } else {
            reject(error);
          }
        };

        processHash.apply(this, [
          hashValue,
          (token: string) => { resolve({ token, attempts }); },
          errorHandler,
          { attempts, debugMessage: debugInfo },
          this.delegateAuthUrl,
          this.shouldRetryWithPopup,
        ]);
      };

      addIframe(delegateAuthUrlWithCd, frameName, handler);
    });
  };

  authenticateWithPopupInternal = authenticateWithPopup;

  authenticateWithPopup = () => new Promise<AuthenticateResolveObject>((resolve, reject) => {
    const msaDelegateToken = cookieGet(cookieName);
    if (msaDelegateToken) {
      resolve({
        token: formatToken(msaDelegateToken),
        attempts: 0,
      });
      return;
    }
    this.authenticateWithPopupInternal(this.delegateAuthUrl, (hashValue?: string) => {
      processHash.apply(this, [
        hashValue,
        (token: string) => { resolve({ token, attempts: 1 }); },
        (err: MsaAuthError) => { reject(err); },
        {},
        this.delegateAuthUrl,
        this.shouldRetryWithPopup,
      ]);
    }, () => {
      // eslint-disable-next-line prefer-promise-reject-errors
      reject('Popup authenticate timed out');
    });
  });
}
